// Plain-language copy for the listing full view: the one-line summary, the area label and the area
// highlights. Built only from data we have (listing facts, RegSO values and neighbourhood scores).
//
// Tone: the popup is someone's home for sale, so weaker areas are never described negatively.
// Strengths are named; average metrics are described as "like nearby areas"; below-average metrics
// are either left out or described by what they are (a lower sqm price is "good value", many rentals
// is "mixed housing"). Raw values are still shown in the stats grid, so nothing is hidden.

import { scoreAdjective, scoreScaleWords, scoreWord } from './scoreWords.js';

// Thousands are separated by a no-break space so figures never wrap mid-number.
const NBSP = '\u00a0';
const formatNumber = (value, digits = 0) => Number(value)
    .toLocaleString('sv-SE', { maximumFractionDigits: digits })
    .replace(/\s/g, NBSP);

// The score's word and a scale's end labels come from scoreWords.js, the one list for the whole site.
export const SCALE_LABELS = {
    sv: scoreScaleWords(true),
    en: scoreScaleWords(false),
};

export const getAreaLabel = (score, swedish) => scoreWord(score, swedish);

const ROOM_WORDS_SV = { 1: 'etta', 2: 'tvåa', 3: 'trea', 4: 'fyra', 5: 'femma', 6: 'sexa' };

// Listing sqm price vs the area's average for the same kind of home, as a fraction (-0.22 = 22 % lower).
// Apartments only: house sqm prices swing with plot, size and condition, so an area average says little
// about one house. Differences beyond -40 %/+60 % usually mean odd data and are not shown.
export const getSqmComparison = ({ listingSqm, isApartment, area }) => {
    const areaSqm = area?.apartmentSqm;
    if (!isApartment || !Number.isFinite(listingSqm) || !Number.isFinite(areaSqm) || areaSqm <= 0) return null;
    const diff = listingSqm / areaSqm - 1;
    if (diff < -0.4 || diff > 0.6) return null;
    return { diff, areaSqm, listingSqm };
};

// "En tvåa på 58 m² i eftertraktade Eriksberg, med ett kvadratmeterpris 22 % under områdets snitt."
export const buildListingSummary = ({ kind, rooms, areaM2, place, combinedScore, sqmComparison, swedish }) => {
    const roomCount = Number(String(rooms ?? '').replace(',', '.'));
    const wholeRooms = Number.isInteger(roomCount) ? roomCount : null;
    const size = Number.isFinite(areaM2) && areaM2 > 0 ? `${formatNumber(areaM2)} m²` : '';
    let subject;
    if (swedish) {
        if (kind === 'apartment') {
            subject = ROOM_WORDS_SV[wholeRooms]
                ? `En ${ROOM_WORDS_SV[wholeRooms]}`
                : `En lägenhet${roomCount > 0 ? ` med ${formatNumber(roomCount, 1)} rum` : ''}`;
            if (size) subject += ` på ${size}`;
        } else {
            subject = kind === 'rowhouse' ? 'Ett radhus' : (kind === 'fritidshus' ? 'Ett fritidshus' : 'En villa');
            if (size) subject += ` på ${size}`;
            if (roomCount > 0) subject += ` med ${formatNumber(roomCount, 1)} rum`;
        }
    } else {
        const noun = kind === 'apartment' ? 'apartment'
            : (kind === 'rowhouse' ? 'row house' : (kind === 'fritidshus' ? 'holiday home' : 'house'));
        subject = roomCount > 0 ? `A ${formatNumber(roomCount, 1)}-room ${noun}` : `A ${noun}`;
        if (size) subject += ` of ${size}`;
    }
    if (!subject) return '';
    let sentence = subject;
    if (place) {
        // The score's own word (scoreWords.js), so the sentence and the score chip agree.
        const adjective = scoreAdjective(combinedScore, swedish);
        sentence += swedish ? ` i ${adjective ? `${adjective} ` : ''}${place}` : ` in ${adjective ? `${adjective} ` : ''}${place}`;
    }
    if (sqmComparison && sqmComparison.diff <= -0.05) {
        const percent = Math.round(-sqmComparison.diff * 100);
        sentence += swedish
            ? `, med ett kvadratmeterpris ${percent} % under områdets snitt`
            : `, priced ${percent}% below the area's average per m²`;
    }
    return `${sentence}.`;
};

// Up to three highlights about the area, strongest first. Each: { id, icon, title, detail }.
export const buildAreaHighlights = (area, { isApartment, swedish }) => {
    if (!area) return [];
    const s = area.scores || {};
    const candidates = [];
    // tier 2 = a strength, 1 = like the neighbours, 0 = neutral description
    const add = (tier, strength, item) => candidates.push({ tier, strength, ...item });

    if (Number.isFinite(area.unemploymentPercent) && s.unemployment != null && s.unemployment >= 3) {
        const value = `${formatNumber(area.unemploymentPercent, 1)}${swedish ? ' ' : ''}%`;
        if (s.unemployment >= 4) {
            add(2, s.unemployment, {
                id: 'unemployment', icon: 'work',
                title: swedish ? 'Få står utan jobb' : 'Few are out of work',
                detail: swedish ? `${value} arbetslöshet, lägre än i närområdet.` : `${value} unemployment, lower than nearby areas.`,
            });
        } else {
            add(1, s.unemployment, {
                id: 'unemployment', icon: 'work',
                title: swedish ? 'Sysselsättning som i närområdet' : 'Employment like nearby areas',
                detail: swedish ? `${value} arbetslöshet.` : `${value} unemployment.`,
            });
        }
    }

    if (Number.isFinite(area.ownedPercent)) {
        const owned = Math.round(area.ownedPercent);
        if (s.owned != null && s.owned >= 4) {
            add(2, s.owned, {
                id: 'owned', icon: 'key',
                title: swedish
                    ? (owned >= 50 ? 'De flesta äger sitt boende' : 'Många äger sitt boende')
                    : (owned >= 50 ? 'Most people own their home' : 'Many own their home'),
                detail: swedish ? `${owned} % av bostäderna är ägda.` : `${owned}% of homes are owned.`,
            });
        } else if (owned >= 60) {
            add(1, 3, {
                id: 'owned', icon: 'key',
                title: swedish ? 'Många äger sitt boende' : 'Many own their home',
                detail: swedish ? `${owned} % av bostäderna är ägda.` : `${owned}% of homes are owned.`,
            });
        } else {
            add(0, 0, {
                id: 'owned', icon: 'key',
                title: swedish ? 'Blandat boende' : 'Mixed housing',
                detail: swedish
                    ? `${owned} % ägda bostäder och ${100 - owned} % hyresrätter.`
                    : `${owned}% owned homes and ${100 - owned}% rentals.`,
            });
        }
    }

    if (Number.isFinite(area.educationPercent) && s.education != null && s.education >= 3) {
        const value = `${formatNumber(area.educationPercent, 0)}${swedish ? ' ' : ''}%`;
        add(s.education >= 4 ? 2 : 1, s.education, {
            id: 'education', icon: 'school',
            title: s.education >= 4
                ? (swedish ? 'Högutbildat grannskap' : 'Well-educated neighbourhood')
                : (swedish ? 'Utbildningsnivå som i närområdet' : 'Education like nearby areas'),
            detail: swedish ? `${value} har eftergymnasial utbildning.` : `${value} have a post-secondary education.`,
        });
    }

    if (Number.isFinite(area.incomeMonthlyTkr) && s.income != null && s.income >= 3) {
        const value = `${formatNumber(area.incomeMonthlyTkr, 1)} ${swedish ? 'tkr/mån' : 'kSEK/month'}`;
        add(s.income >= 4 ? 2 : 1, s.income, {
            id: 'income', icon: 'wallet',
            title: s.income >= 4
                ? (swedish ? 'Hög inkomstnivå' : 'High incomes')
                : (swedish ? 'Inkomster som i närområdet' : 'Incomes like nearby areas'),
            detail: s.income >= 4
                ? (swedish ? `Nettoinkomst ${value} i snitt, över närområdet.` : `Average net income ${value}, above nearby areas.`)
                : (swedish ? `Nettoinkomst ${value} i snitt.` : `Average net income ${value}.`),
        });
    }

    const priceScore = isApartment ? s.apartmentPrice : s.housePrice;
    if (priceScore != null && priceScore <= 2.5) {
        add(2, 7 - priceScore, {
            id: 'price', icon: 'tag',
            title: swedish ? 'Prisvärt område' : 'Good-value area',
            detail: swedish ? 'Kvadratmeterpriserna är lägre än i närområdet.' : 'Prices per m² are lower than in nearby areas.',
        });
    } else if (priceScore != null && priceScore >= 4.5) {
        add(1, priceScore, {
            id: 'price', icon: 'tag',
            title: swedish ? 'Efterfrågat område' : 'In demand',
            detail: swedish ? 'Kvadratmeterpriserna är högre än i närområdet.' : 'Prices per m² are higher than in nearby areas.',
        });
    }

    return candidates
        .sort((a, b) => (b.tier - a.tier) || (b.strength - a.strength))
        .slice(0, 3)
        .map(({ tier, strength, ...item }) => item);
};

// The short note under the area scale comparing this home's sqm price with the area's average for
// apartments. Only says "lower" when it is; otherwise states both figures without judging. The place is
// already in the section heading, so it is not repeated here.
export const buildSqmNote = (comparison, { swedish }) => {
    if (!comparison) return null;
    const percent = Math.round(Math.abs(comparison.diff) * 100);
    const own = formatNumber(comparison.listingSqm);
    const average = formatNumber(comparison.areaSqm);
    const versus = swedish
        ? `${own} mot ${average}${NBSP}kr/m².`
        : `${own} vs ${average}${NBSP}kr/m².`;
    if (comparison.diff <= -0.05) {
        return {
            tone: 'good',
            title: swedish ? `${percent}${NBSP}% under områdets snitt` : `${percent}% below the area average`,
            detail: versus,
        };
    }
    if (Math.abs(comparison.diff) < 0.05) {
        return {
            tone: 'neutral',
            title: swedish ? 'I nivå med områdets snitt' : 'In line with the area average',
            detail: versus,
        };
    }
    return {
        tone: 'neutral',
        title: swedish ? 'Kvadratmeterpris' : 'Price per m²',
        detail: swedish
            ? `${own}${NBSP}kr/m², områdets snitt ${average}${NBSP}kr/m².`
            : `${own}${NBSP}kr/m², area average ${average}${NBSP}kr/m².`,
    };
};
